/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2016, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define(['../VisView',
	'./CrosstabGrid',
	'../../../widgets/livewidget/nls/StringResources',
	'text!./Crosstab.html',
	'../../../DynamicFileLoader',
	'jquery',
	'underscore',
	'../VisEventHandler',
	'./CrosstabEventTarget',
	'../../../util/ContentUtil'
],
function(VisView, CrosstabGrid, StringResources, CrosstabTemplate, DynamicFileLoader, $, _, VisEventHandler, CrosstabEventTarget, ContentUtil) {
	'use strict';

	const ROW_SLOT_ID = 'row_level1';
	const COLUMN_SLOT_ID = 'column_level1';


	const CrosstabView = VisView.extend({
		className: 'dataview crosstab-widget',
		templateString: CrosstabTemplate,
		alwaysRenderOnResize: true,
		/**
		 * Initialize the view and its handlers, then render.
		 */
		init: function() {
			CrosstabView.inherited('init', this, arguments);
			this.visModel.on('change:theme', this.onChangeTheme, this);

			this.isMobilePannable = true;
			this.visProperties = this.visModel.getProperties();
			this.styleProxy = this.visModel.getVisPropertiesCSSProxy();
			this.rowHeadingProperties = this._getPropertiesForPrefix('rowHeading');
			this.columnHeadingProperties = this._getPropertiesForPrefix('columnHeading');
			this.dataValueProperties = this._getPropertiesForPrefix('values');
			this.valueHeadingProperties = this._getPropertiesForPrefix('valueHeading');
			this.summaryProperties = this._getPropertiesForPrefix('summary');
			this.selectedAreaProperties = this._getPropertiesForPrefix('selectedArea');
			this.content = this.content || this.dashboardApi.getCanvas().getContent(this.ownerWidget.getId());
			if(this._isNewConditionalFormatFeature()){
				this._conditionalFormatting = this.content.getFeature('ConditionalFormatting');
			} else {
				// deregister here since the new component do not have access to the dashboardAPI to check whether the feature flag is turned on or off
				// Instead when the new feature gets loaded it always register itself with the 'Properties' and 'CustomColor' feature
				// Remove this code when the new feature get turn on by default
				const newCondFeature = this.content.getFeature('ConditionalFormatting');
				if (newCondFeature) {
					const properties = this.content.getFeature('Properties');
					const customColor = this.dashboardApi.getFeature('CustomColor');
					properties.deregisterProvider(newCondFeature);
					customColor.deregisterProvider(newCondFeature);
				}
			}
			this._crosstabGrid = new CrosstabGrid({
				$el: this.$el.find('.crosstab-widget'),
				visModel: this.visModel,
				dashboardApi: this.dashboardApi,
				colorsService: this.dashboardApi.getFeature('Colors'),
				crosstabView: this,
				conditionalFormatting: this._conditionalFormatting
			});
		},

		onChangeTheme: function() {
			this.visModel.getRenderSequence().reRender();
		},

		getGrid: function() {
			return this._crosstabGrid;
		},

		getDescription: function() {
			// Append the F12 key instruction to the description
			var description = CrosstabView.inherited('getDescription', this, arguments);
			return StringResources.get('WidgetLabelWithDescripion', {
				label: description,
				description: StringResources.get('f12KeyDescription')
			});
		},

		/**
		 * Handle the on container entered event
		 * Sets up keyboard navigation inside the list
		 * @return undefined
		 */
		onEnterContainer: function() {
			this._crosstabGrid.onEnterContainer();
		},

		/**
		 * @Override VisView.getCurrentViewSelector()
		 */
		//TODO: This might need to be abstracted at higher level
		getCurrentViewSelector: function() {
			return this.eventHandler;
		},

		/**
		 * render the results.
		 * @param {Object} renderInfo - renderInfo passed to all render methods from the render sequence.
		 * @returns a promise which is resolved when rendering has completed.
		 */
		render: function(renderInfo) {
			return this._initializeConditionalFormatting().then(() => {
				if (!this.isMappingComplete() || this.hasMissingFilters() || this.hasUnavailableMetadataColumns()) {
					this.removeGridView();
					this.resizeToWidget(renderInfo);
					this.renderIconView();
					return Promise.resolve(this);
				}

				this.removeIconView();
				let dataQueryResult = renderInfo.data.getResult();
				this.setStyles();
				this.setSelections();
				this.setFormat();
				this.setHiddenRowsAndColumns();

				return this._crosstabGrid.render(dataQueryResult, this.resizing, this.getRenderingNewData(), this.eventHandler).then(isRenderValid => {
					if (!isRenderValid) {
						return { reRenderNeeded: true };
					} else {
						this.resizeToWidget(renderInfo);
						this.visControl.resetData(this.visModel);
						this.visControl.setGridData(this._crosstabGrid);
						//Note: renderComplete is called from VisView.
						return CrosstabView.inherited('render', this, arguments);
					}
				});
			});
		},

		removeGridView: function() {
			this._crosstabGrid && this._crosstabGrid.removeGridView();
		},

		remove: function() {
			if (this._crosstabGrid) {
				this._crosstabGrid.remove();
			}
			if (this.eventHandler) {
				this.eventHandler.remove();
			}

			this.visControl = null;
			// The super.destroy will clear all members of this class.
			CrosstabView.inherited('remove', this, arguments);
		},

		_isNewConditionalFormatFeature () {
			return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled');
		},

		_initializeConditionalFormatting: function() { // This is temporary until features have a proper way of initializing themselves
			return this._conditionalFormatting ? this._conditionalFormatting.initializeFeature() : Promise.resolve();
		},

		_getPropertiesForPrefix: function (prefix) {
			var propertiesForPrefix = this.visProperties.filter(function (property) {
				return property.prefix === prefix;
			});
			return propertiesForPrefix;
		},

		/**
		 * Construct a CSS style map for the properties changed
		 * @returns  {object} in the form of {<styleName>: <propertyValue>}
		 */
		_constructTextStyles: function (properties) {
			let stylesMap = {};
			const visModel = this.visModel;
			const styleProxy = this.styleProxy;
			const styleToValue = styleProxy.styleToValue;
			_.each(properties, (property) => {
				// one property may control multiple styles
				let styleNames = styleProxy.getPropertyStyle(property.id);
				styleNames = Array.isArray(styleNames) ? styleNames : [styleNames];
				let propertyValue = visModel.getPropertyValue(property.id);
				_.each(styleNames, (styleName) => {
					if(propertyValue){
						if(styleToValue && styleToValue[styleName]){
							if (typeof propertyValue === 'object') {
								let result = {};
								_.each(propertyValue, (val, key) => {
									result[key] = {
										value: val.value ? styleToValue[styleName] : '',
										weight: val.weight
									};
								});
								propertyValue = result;
							} else {
								propertyValue = styleToValue[styleName];
							}
						}
						stylesMap[styleName] = propertyValue;
					} else {
						stylesMap[styleName] = '';
					}
				});
			});
			return stylesMap;
		},

		setStyles: function() {
			const styleObj = {
				rowHeadingStyles: this._constructTextStyles(this.rowHeadingProperties),
				columnHeadingStyles: this._constructTextStyles(this.columnHeadingProperties),
				dataValueStyles: this._constructTextStyles(this.dataValueProperties),
				valueHeadingStyles: this._constructTextStyles(this.valueHeadingProperties),
				summaryStyles: this._constructTextStyles(this.summaryProperties),
				selectedAreaStyles: this._constructTextStyles(this.selectedAreaProperties)
			};
			this._crosstabGrid.setStyles(styleObj);
		},



		/**
		 * Loads and creates the control. Returns a promise which is resolved when the control
		 * is created and ready to render
		 */
		whenVisControlReady: function() {

			//Load the control class defined in the item definition (eg JQGrid).
			this.$el.addClass('dataview crosstab-widget');

			return DynamicFileLoader.load(['dashboard-analytics/visualizations/renderer/crosstab/CrosstabControl'])
				.then((modules) => {
					var CrosstabControl = modules[0];
					var visControl = new CrosstabControl({
						domNode: this.$el.find('.crosstab-widget')[0],
						visualization: this.visualization
					});
					this.visControl = visControl;
					this.visControl.resetData(this.visModel);
					this.eventHandler = new VisEventHandler({
						target: new CrosstabEventTarget({
							$el: this.$el,
							view: this,
							visAPI: this.visModel.ownerWidget.visAPI,
							content: this.content,
							visualization: this.visualization
						}),
						dashboardSettings: this.dashboardSettings,
						transaction: this.transactionApi,
						logger: this.logger,
						ownerWidget: this.ownerWidget,
						visAPI: this.ownerWidget.visAPI
					});
					return this.visControl;
				});
		},

		/**
		 * Update the selection tuple of the crosstabGrid
		 */
		setSelections() {
			const aRowItems = ContentUtil.getColumnIdList([this.visualization.getSlots().getSlot(ROW_SLOT_ID)], 'attribute');
			const aColItems = ContentUtil.getColumnIdList([this.visualization.getSlots().getSlot(COLUMN_SLOT_ID)], 'attribute');
			const aRowSelection = this.getSelector().getSelection(aRowItems, {
				includeParents: true
			});
			const aColSelection = this.getSelector().getSelection(aColItems, {
				includeParents: true
			});
			const aDatapointSelection = this.getSelector().getSelection(aColItems.concat(aRowItems));

			//only clear the selection caches if we've just made a selection and we currently have items in the caches;
			const options = {
				clearSelectionCaches: (aRowSelection || aColSelection || aDatapointSelection) && (this._crosstabGrid.isSummaryHighlighted() || this._crosstabGrid.getSelectedMeasureNodes())
			};

			this._crosstabGrid.setSelections({
				rowSelection: aRowSelection,
				colSelection: aColSelection,
				datapointSelection: aDatapointSelection
			}, options);
		},

		/**
		 * set property value with contentAPI
		 */
		setPropertyValue: function(property, value, options) {
			this.content.setPropertyValue(property, value, options);
		},


		/**
		 * Update the selection tuple of the crosstabGrid and update the view
		 * this will be called from visView once grid view is ready
		 * should never be called before view is ready
		 */
		renderSelected() {
			this.setSelections();
			// force a re-render so selections are handled properly
			if(this._crosstabGrid){
				this._crosstabGrid.onResize();
			}
		},

		/**
		 * get property value with contentAPI
		 */
		getPropertyValue: function(property) {
			return this.content && this.content.getPropertyValue(property);
		},


		/**
		 * Update the format info of the crosstab
		 */
		setFormat: function() {
			const format = {
				fixedRowHeight : this.getPropertyValue('fixedRowHeight'),
				fixedColumnWidth : this.getPropertyValue('fixedColumnWidth'),
				columnWidths: this.getPropertyValue('columnWidths'),
				rowHeights: this.getPropertyValue('rowHeights')
			};
			this._crosstabGrid.setFormat(format);
		},

		/**
		 * Update the hidden rows and columns info of the crosstab
		 */
		setHiddenRowsAndColumns: function() {
			const hiddenRowsAndColumns = this.getPropertyValue('hiddenRowsAndColumns') || [];
			this._crosstabGrid.setHiddenRowsAndColumns(hiddenRowsAndColumns);
		},

		/**
		 * build validation response
		 * @param {boolean} isValid
		 * @param {string} message
		 * @returns {object}
		 */
		_buildValidateResponse: function(isValid, message) {
			return { isValid, message };
		},

		/**
		 * validate the input for property input
		 * @param {number} propertyValue
		 * @param {number} range
		 */
		_checkValidInput: function(propertyValue, range) {
			if (!$.isNumeric(propertyValue)) {
				return this._buildValidateResponse(false, StringResources.get('prop_sizeInputNonNumericValue'));
			}
			if (parseInt(propertyValue) < range) {
				return this._buildValidateResponse(false, StringResources.get('prop_sizeValueShouldBeHigher', {
					value: range
				}));
			}
			return this._buildValidateResponse(true);
		},

		/**
		 * Property Provider for crosstabView
		 */
		getPropertyList: function() {
			// TODO livewidget_cleanup - remove this line after making sure it is not used
			throw new Error('should not be called');
		},

		/**
		 * Property Provider for crosstabView
		 */
		getPropertyLayoutList: function() {
			// TODO livewidget_cleanup - remove this line after making sure it is not used
			throw new Error('should not be called');
		},

		/**
		 * Override the animate to avoid the fade in/out when we resize or apply filters
		 */
		animate: function() {
			if (this.resizing) {
				this.visModel.renderCompleteBeforeAnimation(); // Rendering is complete, no animation
			} else {
				CrosstabView.inherited('animate', this, arguments);
			}
		}

	});
	return CrosstabView;
});
